// src/pages/ValuationPage.jsx
import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  TextField,
  Button,
  Chip,
  Stack,
  Snackbar,
  Alert,
  useTheme,
  useMediaQuery,
  Stepper,
  Step,
  StepLabel,
  InputAdornment,
  Divider
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import HotelIcon from '@mui/icons-material/Hotel';
import BathtubIcon from '@mui/icons-material/Bathtub';
import VerifiedIcon from '@mui/icons-material/Verified';
import SpeedIcon from '@mui/icons-material/Speed';
import InsightsIcon from '@mui/icons-material/Insights';

const API_BASE = process.env.REACT_APP_API_BASE || '/api';

const steps = ['Property Details', 'Your Information', 'Review & Submit'];
const heroImage = '/media/hero-property-poster.jpg';

// Custom step icons
function StepIcon(props) {
  const { active, completed, icon } = props;
  const map = {
    1: <HomeIcon />,
    2: <PersonIcon />,
    3: <CheckCircleIcon />
  };
  return (
    <Box
      sx={{
        width: 36,
        height: 36,
        borderRadius: '50%',
        display: 'grid',
        placeItems: 'center',
        color: completed ? 'common.white' : active ? 'primary.main' : 'text.secondary',
        bgcolor: completed ? 'primary.main' : active ? 'primary.light' : 'action.hover',
        boxShadow: active ? 4 : 0,
        transition: 'all .2s ease'
      }}
    >
      {map[icon]}
    </Box>
  );
}

export default function ValuationPage() {
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down('sm'));

  const [activeStep, setActiveStep] = useState(0);
  const [form, setForm] = useState({
    address: '',
    postcode: '',
    bedrooms: '',
    bathrooms: '',
    name: '',
    email: '',
    phone: '',
    notes: '',
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleChange = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const validateStep = (step) => {
    const errs = {};
    if (step === 0) {
      if (!form.address.trim()) errs.address = 'Address is required';
      if (!form.postcode.trim()) errs.postcode = 'Postcode is required';
    }
    if (step === 1) {
      if (!form.name.trim()) errs.name = 'Name is required';
      if (!/^\S+@\S+\.\S+$/.test(form.email)) errs.email = 'Valid email required';
      if (!form.phone.trim()) errs.phone = 'Phone is required';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) setActiveStep((s) => s + 1);
  };
  const handleBack = () => setActiveStep((s) => s - 1);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep(activeStep)) return;
    setSubmitting(true);
    try {
      setSubmitError('');
      const res = await fetch(`${API_BASE}/valuation_request.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json.ok === false) throw new Error(json.error || `HTTP ${res.status}`);
      setSuccessOpen(true);
      setActiveStep(0);
      setForm({
        address: '',
        postcode: '',
        bedrooms: '',
        bathrooms: '',
        name: '',
        email: '',
        phone: '',
        notes: '',
      });
    } catch (error) {
      setSubmitError(error.message || 'Unable to submit your valuation request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Property Address"
              value={form.address}
              onChange={handleChange('address')}
              error={!!errors.address}
              helperText={errors.address}
              fullWidth
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LocationOnIcon />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Postcode"
              value={form.postcode}
              onChange={handleChange('postcode')}
              error={!!errors.postcode}
              helperText={errors.postcode}
              fullWidth
              required
            />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="Bedrooms"
                  type="number"
                  value={form.bedrooms}
                  onChange={handleChange('bedrooms')}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <HotelIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Bathrooms"
                  type="number"
                  value={form.bathrooms}
                  onChange={handleChange('bathrooms')}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <BathtubIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        );
      case 1:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Your Name"
              value={form.name}
              onChange={handleChange('name')}
              error={!!errors.name}
              helperText={errors.name}
              fullWidth
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Email"
              type="email"
              value={form.email}
              onChange={handleChange('email')}
              error={!!errors.email}
              helperText={errors.email}
              fullWidth
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Phone"
              value={form.phone}
              onChange={handleChange('phone')}
              error={!!errors.phone}
              helperText={errors.phone}
              fullWidth
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIphoneIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        );
      case 2:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Additional Notes"
              value={form.notes}
              onChange={handleChange('notes')}
              multiline
              minRows={3}
              fullWidth
            />
            <Typography variant="body2" color="text.secondary">
              We’ll review your details and be in touch with a free, no-obligation valuation.
            </Typography>
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <>
      {/* HERO */}
      <Box
        sx={{
          position: 'relative',
          py: { xs: 8, md: 14 },
          textAlign: 'center',
          color: 'common.white',
          backgroundImage: `linear-gradient(rgba(8,16,24,.50), rgba(8,16,24,.50)), url(${heroImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          overflow: 'hidden'
        }}
      >
        {/* Animated soft highlight */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            background: 'radial-gradient(60% 40% at 50% 0%, rgba(255,255,255,.20) 0%, rgba(255,255,255,0) 60%)',
            mixBlendMode: 'soft-light',
            animation: 'floatGlow 9s ease-in-out infinite alternate',
            '@keyframes floatGlow': {
              from: { transform: 'translateY(-6px)' },
              to: { transform: 'translateY(6px)' }
            }
          }}
        />
        <Container sx={{ position: 'relative', zIndex: 1 }}>
          <Typography variant={isSm ? 'h4' : 'h2'} fontWeight={800} gutterBottom sx={{ letterSpacing: .3 }}>
            Free property valuation
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.95 }}>
            Request a no-obligation market appraisal from our local team
          </Typography>

          {/* Trust strip */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            justifyContent="center"
            alignItems="center"
            sx={{ mt: 3 }}
          >
            <Chip icon={<VerifiedIcon />} label="Local market knowledge" sx={{ bgcolor: '#fff' }} />
            <Chip icon={<InsightsIcon />} label="Sales and rental valuations" sx={{ bgcolor: '#fff' }} />
            <Chip icon={<SpeedIcon />} label="No obligation" sx={{ bgcolor: '#fff' }} />
          </Stack>
        </Container>
      </Box>

      {/* MAIN */}
      <Container maxWidth="lg" sx={{ mt: -8, mb: 8, position: 'relative', zIndex: 2 }}>
        <Grid container spacing={4}>
          {/* Left: Benefits & Stats */}
          <Grid item xs={12} md={5}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                backdropFilter: 'blur(6px)',
                background: theme.palette.mode === 'dark'
                  ? 'linear-gradient(180deg, rgba(255,255,255,.04), rgba(255,255,255,.02))'
                  : 'linear-gradient(180deg, rgba(255,255,255,.75), rgba(255,255,255,.55))',
                border: '1px solid',
                borderColor: 'divider'
              }}
            >
              <Typography variant="h5" fontWeight={800} gutterBottom>
                Why choose MRM Lettings?
              </Typography>

              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Chip icon={<HomeIcon />} label="Local expertise" />
                <Chip icon={<AttachMoneyIcon />} label="Accurate valuations" />
                <Chip icon={<CalendarTodayIcon />} label="Fast turnaround" />
              </Stack>

              <Divider sx={{ my: 3 }} />

              <Stack spacing={2}>
                <Stack direction="row" spacing={1.5}><HomeIcon color="primary" /><Typography>Advice based on your property and local market conditions.</Typography></Stack>
                <Stack direction="row" spacing={1.5}><AttachMoneyIcon color="primary" /><Typography>Clear guidance on likely sale or rental value.</Typography></Stack>
                <Stack direction="row" spacing={1.5}><CalendarTodayIcon color="primary" /><Typography>A convenient follow-up from our Middlesbrough office.</Typography></Stack>
              </Stack>
            </Paper>
          </Grid>

          {/* Right: Wizard Form */}
          <Grid item xs={12} md={7}>
            <Paper
              elevation={8}
              sx={{
                p: { xs: 3, md: 5 },
                borderRadius: 3,
                backdropFilter: 'blur(10px)',
                backgroundColor: theme.palette.mode === 'dark'
                  ? 'rgba(20,24,28,0.65)'
                  : 'rgba(255,255,255,0.85)',
                border: '1px solid',
                borderColor: 'divider'
              }}
            >
              <Typography variant="h5" fontWeight={800} gutterBottom>
                Request your valuation
              </Typography>

              <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
                {steps.map((label, i) => (
                  <Step key={label}>
                    <StepLabel StepIconComponent={StepIcon}>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>

              <Box
                component="form"
                noValidate
                autoComplete="off"
                onSubmit={
                  activeStep === steps.length - 1
                    ? handleSubmit
                    : (e) => {
                        e.preventDefault();
                        handleNext();
                      }
                }
                sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
              >
                {getStepContent(activeStep)}

                {submitError && <Alert severity="error">{submitError}</Alert>}

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                  <Button
                    onClick={handleBack}
                    disabled={activeStep === 0 || submitting}
                    variant="text"
                    sx={{
                      px: 2,
                      '&:hover': { transform: 'translateX(-1px)' },
                      transition: 'transform .15s ease'
                    }}
                  >
                    Back
                  </Button>

                  <Button
                    type="submit"
                    variant="contained"
                    disabled={submitting}
                    sx={{
                      px: 3,
                      boxShadow: 4,
                      '&:active': { transform: 'translateY(1px)' },
                      transition: 'transform .12s ease'
                    }}
                  >
                    {activeStep === steps.length - 1
                      ? (submitting ? 'Submitting…' : 'Get My Valuation')
                      : 'Next'}
                  </Button>
                </Box>
              </Box>
            </Paper>

            {/* Trust badges */}
            {/* <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2, opacity: 0.9, flexWrap: 'wrap' }}>
              <Typography variant="caption" color="text.secondary">As featured in</Typography>
              <Chip size="small" label="Rightmove" variant="outlined" />
              <Chip size="small" label="Zoopla" variant="outlined" />
              <Chip size="small" label="OnTheMarket" variant="outlined" />
            </Stack> */}
          </Grid>
        </Grid>
      </Container>

      {/* Success Snackbar */}
      <Snackbar
        open={successOpen}
        autoHideDuration={4800}
        onClose={() => setSuccessOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="success"
          onClose={() => setSuccessOpen(false)}
          sx={{ width: '100%' }}
          icon={<CheckCircleIcon fontSize="inherit" />}
        >
          Thanks! Your valuation request has been sent.
        </Alert>
      </Snackbar>
    </>
  );
}
